/**
 * The frame every signed-in page sits in: navigation on the left, search and "New" along the
 * top, the page in the middle. The navigation runs in the order of a seller's day: the work
 * (Home, Agenda, Pipeline, Calendar), the book (each record kind beside its identity hue), the
 * numbers. Counts are the things that should pull an agent's eye: late follow-ups, new leads
 * waiting, calls nobody has matched. Below 1280px it folds to an icon rail.
 */
import { roleLabel } from "@tlcrm/shared";
import { useEffect, useState } from "react";
import { NavLink, Outlet } from "react-router";

import { useCockpit, useTasks, useUnmatchedCalls } from "../api/hooks";
import { useSession, useMe } from "../auth/session";
import { CommandBar } from "../components/CommandBar";
import { Icon } from "../components/Icon";
import type { IconName } from "../components/icons";
import { KindDot, type RecordKind } from "../components/kit";
import { initials } from "../lib/format";
import { NewRecordMenu } from "./NewRecordMenu";
import { ShortcutSheet } from "./shortcuts";
import { useShortcuts } from "./useShortcuts";

function Item({
  to,
  icon,
  label,
  count,
  alert,
  kind,
}: {
  to: string;
  icon: IconName;
  label: string;
  count?: number;
  alert?: boolean;
  kind?: RecordKind;
}) {
  return (
    <NavLink to={to} end={to === "/"} className="nav-item" title={label}>
      <Icon name={icon} />
      <span className="nav-label">{label}</span>
      {kind && <KindDot kind={kind} />}
      {count ? (
        <span className={`nav-count${alert ? " is-alert" : ""}`}>
          {count}
          {alert && <span className="sr-only"> late</span>}
        </span>
      ) : null}
    </NavLink>
  );
}

export function Shell() {
  const { me, can } = useMe();
  const { signOut } = useSession();
  const [palette, setPalette] = useState(false);
  const [sheet, setSheet] = useState(false);
  const overdue = useTasks({ due: "overdue" });
  const unmatched = useUnmatchedCalls();
  const cockpit = useCockpit();

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setPalette((p) => !p);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);
  useShortcuts({ search: () => setPalette(true), help: () => setSheet((v) => !v) });

  const mac = /Mac/.test(navigator.platform);

  return (
    <div className="crm">
      <nav className="nav" aria-label="Main">
        <div className="nav-brand">
          <span className="nav-mark">TL</span>
          <div>
            <b>Tomorrow</b>
            <span>CRM</span>
          </div>
        </div>
        <div className="nav-section eyebrow">Work</div>
        <Item to="/" icon="home" label="Home" />
        <Item to="/tasks" icon="checkSquare" label="Agenda" count={overdue.data?.length} alert />
        <Item to="/pipeline" icon="layers" label="Pipeline" />
        <Item to="/calendar" icon="calendar" label="Calendar" />
        <div className="nav-section eyebrow">Book</div>
        <Item
          to="/leads"
          icon="inbox"
          label="Leads"
          kind="lead"
          count={cockpit.data?.waiting.count}
        />
        <Item to="/contacts" icon="users" label="Customers" kind="customer" />
        <Item to="/policies" icon="briefcase" label="Policies" kind="policy" />
        <Item to="/calls" icon="phoneIn" label="Unmatched calls" count={unmatched.data?.length} />
        <div className="nav-section eyebrow">Numbers</div>
        <Item to="/dashboards" icon="chart" label="Dashboards" />
        <Item to="/reports" icon="list" label="Reports" />
        {can("commission:read") && <Item to="/commissions" icon="dollar" label="Commissions" />}
        {can("admin:lists") && (
          <>
            <div className="nav-section eyebrow">Admin</div>
            <Item to="/admin" icon="shield" label="Settings and users" />
          </>
        )}
        <div className="nav-spacer" />
        <div className="nav-me">
          <span className="avatar">{initials(me.user.fullName)}</span>
          <div className="who">
            <b className="truncate">{me.user.fullName}</b>
            <span>{roleLabel(me.user.role)}</span>
          </div>
          <button
            className="icon-btn"
            onClick={() => void signOut()}
            aria-label="Sign out"
            title="Sign out"
          >
            <Icon name="logOut" />
          </button>
        </div>
      </nav>
      <header className="topbar">
        <button className="cmdk" onClick={() => setPalette(true)}>
          <Icon name="search" />
          Search people and policies
          <span className="keys">
            <kbd>{mac ? "⌘" : "Ctrl"}</kbd>
            <kbd>K</kbd>
          </span>
        </button>
        <div className="topbar-spacer" />
        <button
          className="btn btn-ghost btn-sm topbar-keys"
          onClick={() => setSheet(true)}
          title="Keyboard shortcuts"
        >
          <kbd className="keycap">?</kbd>
          Shortcuts
        </button>
        <NewRecordMenu />
      </header>
      <main className="main" id="main">
        <Outlet />
      </main>
      {palette && <CommandBar onClose={() => setPalette(false)} />}
      {sheet && <ShortcutSheet onClose={() => setSheet(false)} />}
    </div>
  );
}
